import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, Inject, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { fromEvent, Subject, takeUntil, timer } from 'rxjs';

import { MediaQuery } from '../../services/media-query.service';
import { DropdownBase } from './dropdown.base';
import { menuTransition } from './dropdown.transitions';
import { WindowService } from 'core/services/window.service';

@Component({
  selector: 'app-dropdown',
  template: `
    <ng-content select="[dpwAnchor]"></ng-content>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayMinWidth]="width || originWidth"
      [cdkConnectedOverlayWidth]="width || originWidth"
      [cdkConnectedOverlayPush]="overlayPush"
      [cdkConnectedOverlayViewportMargin]="6"
      [cdkConnectedOverlayPositions]="overlayPosition"
      [cdkConnectedOverlayOrigin]="overlayOrigin"
      [cdkConnectedOverlayOpen]="isActive()">
      <div
        dpwOverlay
        class="dpw-overlay {{ mobileAppearance }} {{ overlayClass }} th{{ theme }}"
        [@menuTransition]="transitionState">
        @if (mobileAppearance === 'drawer' || mobileAppearance === 'inset-top') {
          <div
            class="drawer-handler"
            *mediaIf="'small only'"
            appOnInit
            (initialized)="observeHandler(handler)"
            (destroyed)="unobserveHandler()"
            #handler></div>
        }
        <div (mousedown)="stopClose()" style="display: contents;">
          <ng-content></ng-content>
        </div>
      </div>
    </ng-template>
  `,
  animations: [menuTransition],
  styleUrls: ['./styles/dropdown.scss'],
  providers: [
    {
      provide: DropdownBase,
      useExisting: forwardRef(() => DropdownComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent extends DropdownBase implements OnInit {
  private _prevTouchY = 0;
  private _totalSlideOffsetY = 0;
  private _slideDirection?: 'up' | 'down';
  private _handlerSubject = new Subject<void>();

  constructor(
    media: MediaQuery,
    renderer: Renderer2,
    router: Router,
    elementRef: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) document: Document,
  ) {
    super(media, renderer, router, elementRef, document);
    renderer.addClass(elementRef.nativeElement, 'app-control');
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  observeHandler(handler: HTMLElement) {
    this._handlerSubject.next();
    fromEvent(handler, 'touchstart')
      .pipe(takeUntil(this._handlerSubject), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.onHandlerTouchStart(event as TouchEvent);
        event.preventDefault();
      });
    fromEvent(handler, 'touchmove')
      .pipe(takeUntil(this._handlerSubject), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.onHandlerTouchmove(event as TouchEvent);
        event.preventDefault();
      });
    fromEvent(handler, 'touchend')
      .pipe(takeUntil(this._handlerSubject), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.onHandlerTouchEnd();
      });
  }

  unobserveHandler() {
    this._handlerSubject.next();
  }

  onHandlerTouchStart(event: TouchEvent) {
    this._prevTouchY = event.touches[0].clientY;
    this._totalSlideOffsetY = 0;
  }

  onHandlerTouchmove(event: TouchEvent) {
    const touchY = event.touches[0].clientY;
    const diff = this._prevTouchY - touchY;
    this._prevTouchY = touchY;
    this._totalSlideOffsetY -= diff;
    const element = this.cdkOverlay.overlayRef.overlayElement.firstElementChild as HTMLElement;
    if (element && this._totalSlideOffsetY >= 0) {
      this._slideDirection = diff > 0 ? 'up' : 'down';
      element.style.transform = `translateY(${Math.max(0, this._totalSlideOffsetY)}px)`;
    } else {
      this._slideDirection = undefined;
    }
  }

  onHandlerTouchEnd() {
    const element = this.cdkOverlay.overlayRef.overlayElement.firstElementChild as HTMLElement;
    if (!element) {
      return;
    }
    const maxDuration = 330;
    const durationFactor = Math.max(0, this._totalSlideOffsetY) / (element.clientHeight + 50);
    if (this._slideDirection === 'down' && this._totalSlideOffsetY > 30) {
      const duration = maxDuration * (1 - durationFactor);
      this._animateDropdown(duration, element.clientHeight + 50, Math.max(0, this._totalSlideOffsetY), element);
      timer(duration)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.cdkOverlay.overlayRef?.overlayElement.firstElementChild?.remove();
          this.close();
        });
    } else {
      this._animateDropdown(maxDuration * durationFactor, 0, Math.max(0, this._totalSlideOffsetY), element);
    }
  }

  private _animateDropdown(duration: number, targetY: number, initialY: number, element: HTMLElement) {
    let start: number | null = null;
    const _self = this;
    const distance = targetY - initialY;
    function step(timestamp: number) {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      // Calculate the progress as a fraction of the total duration
      const progress = Math.min(elapsed / duration, 1); // Ensure progress doesn't exceed 1
      const newY = initialY + distance * progress;
      element.style.transform = `translateY(${newY}px)`;
      if (progress < 1) {
        _self.window.requestAnimationFrame(step);
      }
    }
    this.window.requestAnimationFrame(step);
  }
}
